<script setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useLogsStore } from '@/stores/logs'
import calcDatas from '@/helpers/calcDatas'

const props = defineProps({
    uuid: {
        type: String
    }
})

const translaterStore   = useTranslaterStore()
const logsStore         = useLogsStore()

const getTranslate  = computed(() => translaterStore.getTranslate)
const logs          = computed(() => logsStore.logs)
const logsProcess   = computed(() => logsStore.logsProcess)
const logsMsg       = computed(() => logsStore.logsMsg)

const inputSearch       = ref('')
const pageNumber        = ref(1)
const logsMode          = ref('user')
const logsFilter        = ref(null)
const showFilterGRPC    = ref(true)
const showFilterEGTS    = ref(false)
const UTAUserLogsFrom   = ref(calcDatas('month'))
const UTAUserLogsTill   = ref(calcDatas('now'))

function changePage(cursor, dir) {

    const params = {

        'uuid_user_array': props.uuid,
        'c.limit': 10, 
        'c.direction': dir, 
        'c.cursor': cursor,
        'data': logsMode.value,
        'start': Math.round(new Date(UTAUserLogsFrom.value).getTime() / 1000),
        'stop': Math.round(new Date(UTAUserLogsTill.value).getTime() / 1000),
        'c.order': 'DESC'
    }

    if(inputSearch.value) 
        params['c.q'] = inputSearch.value

    logsStore.readLogs(params).then(() => {
        dir == 'after' ? 
            pageNumber.value++ : 
            pageNumber.value--
    })
}

/**
 * Здесь не используется передача события отправки
 * для большего удобства работы с фильтрами [filterLogs]
 */
function submitForm() {

    let data = {
        data: logsMode.value, 
        'c.limit': 10,
        'c.order': 'DESC',
        uuid_user_array: props.uuid
    }

    if(UTAUserLogsFrom.value && UTAUserLogsTill.value) {
        data.start  = Math.round(new Date(UTAUserLogsFrom.value).getTime() / 1000)
        data.stop   = Math.round(new Date(UTAUserLogsTill.value).getTime() / 1000)
    }

    if(logsFilter.value) {
        data.action = logsFilter.value
    }

    pageNumber.value = 1
    logsStore.readLogs(data)
}

logsStore.readLogs({
    data: logsMode.value, 
    'start': Math.round(new Date(UTAUserLogsFrom.value).getTime() / 1000),
    'stop': Math.round(new Date(UTAUserLogsTill.value).getTime() / 1000),
    'c.limit': 10,
    'c.order': 'DESC',
    uuid_user_array: props.uuid
})

onBeforeUnmount(() => {
    logsStore.clearLogs()
})

watch(logs, () => {
    showFilterGRPC.value = true
    showFilterEGTS.value = false
})
</script>

<template>
    <div class="mb-5 uta-user-info">

        <header class="page__header uta-header" role="heading" aria-level="1">
            <h1 class="uta-header__title">{{ getTranslate.USER_LOGS }}</h1>
            <div class="spinner-border uta-header__spinner" role="status" v-if="logsProcess && logs.array?.length">
                <span class="visually-hidden">Loading...</span>
            </div>
        </header>

        <form action="/" id="logsForm" @submit.prevent="submitForm()">
            
            <div class="row d-flex align-items-end">
                <div class="col-12 col-sm-6 col-md-4">
                    <div class="form-group mb-4">
                        <div class="uta-user-info__prop-title">
                            {{  getTranslate.HISTORY_START }}
                        </div>
                        <input 
                            class="uta-input uta-input--block" 
                            type="datetime-local" 
                            id="UTAUserLogsFrom"
                            v-model="UTAUserLogsFrom"
                        >
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-md-4">
                    <div class="form-group mb-4">
                        <div class="uta-user-info__prop-title">
                            {{  getTranslate.HISTORY_END }}
                        </div>
                        <input 
                            class="uta-input uta-input--block" 
                            type="datetime-local" 
                            id="UTAUserLogsTill"
                            v-model="UTAUserLogsTill"
                        >
                    </div>
                </div>
                <div class="col-12 col-md-4">
                    <button class="uta-btn uta-btn--block uta-btn--lg uta-btn--main mb-4" type="submit">
                        <template v-if="!logsProcess">
                            {{ getTranslate.BTN_REQUEST }}
                        </template>
                        <template v-else>
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </template>
                    </button>
                </div>
            </div>

            <div 
                v-if="logsMsg && !logsMsg.success"
                class="mt-4 uta-alert uta-logs__error"
                :class="{
                    'uta-alert--success': logsMsg.success,
                    'uta-alert--danger': !logsMsg.success
                }"
            >
                <span class="material-icons uta-alert__icon">
                    {{ logsMsg.success ? 'check_circle_outline' : 'error_outline' }}
                </span>
                <span class="alert__text">{{ getTranslate[logsMsg.text] }}</span>
            </div>

            <template v-if="logs.array">

                <div class="row mt-4 mb-0 d-none d-sm-flex">

                    <div class="col-12 col-sm-8">
                        <div class="uta-user-info__prop-title">
                            {{ getTranslate.USER_ACTION }}
                        </div>
                    </div>

                    <div class="col-12 col-sm-4">
                        <div class="uta-user-info__prop-title uta-user-info__prop-title--datetime">
                            {{ getTranslate.USER_DATETIME }}
                        </div>
                    </div>
                </div>

                <div class="accordion" id="logsAccordion" v-if="logs.array.length">

                    <div class="accordion-item uta-logs__log" v-for="(log, index) in logs.array" :key="index">

                        <div 
                            class="collapsed uta-logs__log-header uta-logs__log-header--user" 
                            :id="'logsAccordion_collapse_' + index" 
                            data-bs-toggle="collapse" 
                            :data-bs-target="'#collapse' + index" 
                            aria-expanded="false" 
                            :aria-controls="'collapse' + index"
                        >

                            <div class="row" style="width: 100%">
                                <div class="col-12 col-sm-8 mb-4 mb-sm-0">
                                    <span class="d-inline d-sm-none">{{ getTranslate.USER_ACTION }}: </span>
                                    {{ log.message }}
                                </div>
                                <div class="col-12 col-sm-4">
                                    <span class="d-inline d-sm-none">{{ getTranslate.USER_DATETIME }}: </span>
                                    {{ new Date(Number(log.createdAt / 1000)).toLocaleString() }}
                                </div>
                            </div>
                            
                            <span class="material-icons">
                                expand_less
                            </span>

                        </div>

                        <div 
                            :id="'collapse' + index" 
                            class="uta-logs__log-body collapse" 
                            :aria-labelledby="'logsAccordion_collapse_' + index" 
                            data-bs-parent="#logsAccordion"
                        >

                            <div class="uta-list">

                                <div class="uta-list__item list__item--first">
                                    <div class="row">
                                        <div class="col-4">
                                            {{ getTranslate.USER_LOGS_DATE }}: 
                                        </div>
                                        <div class="col-8">
                                            {{ new Date(Number(log.createdAt / 1000)).toLocaleString() }}
                                        </div>
                                    </div>
                                </div>

                                <div class="uta-list__item">
                                    <div class="row">
                                        <div class="col-4">
                                            {{ getTranslate.USER_LOGS_ACTION }}: 
                                        </div>
                                        <div class="col-8">
                                            {{ log.action }}
                                        </div>
                                    </div>
                                </div>

                                <div class="uta-list__item">
                                    <div class="row">
                                        <div class="col-4">
                                            {{ getTranslate.USER_LOGS_LEVEL }}:
                                        </div>
                                        <div class="col-8">
                                            {{ log.level }}
                                        </div>
                                    </div>
                                </div>

                                <div class="uta-list__item">
                                    <div class="row">
                                        <div class="col-4">
                                            {{ getTranslate.USER_LOGS_DETAILS }}:
                                        </div>
                                        <div class="col-8" style="word-wrap: break-word;">
                                            <template v-if="Object(log.details)">
                                                <div v-for="(detail, index) in log.details" :key="index" class="mb-4">
                                                    {{ index }}: {{ detail }}
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>

                </div>

                <div class="row" v-if="logs.array.length">
                    <div class="uta-cars__pagination" v-if="logs.c && (logs.c.isBefore || logs.c.isAfter)">
                        <div class="row">
                            <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                                <div class="uta-pagination">
                                    <button 
                                        :disabled="logsProcess || !logs.c.isBefore"
                                        class="uta-btn uta-btn--sm uta-pagination__prev" 
                                        @click.prevent="changePage(logs.c.start, 'before')"
                                    >
                                        <span class="material-icons">
                                            navigate_before
                                        </span>
                                    </button>
                                    <div class="uta-pagination__current">
                                        {{ pageNumber }}
                                    </div>
                                    <button 
                                        :disabled="logsProcess || !logs.c.isAfter"
                                        class="uta-btn uta-btn--sm uta-pagination__next" 
                                        @click.prevent="changePage(logs.c.stop, 'after')"
                                    >
                                        <span class="material-icons">
                                            navigate_next
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </template>

        </form>

    </div>

</template>

<style lang="scss">
@import '@/assets/styles/scss/components/logs.scss';
</style>